import { createContext, useContext } from 'react';

/**
 * Provides a way to create typesafe contexts without having to check for null
 * initial values all of the time.
 * @see https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/context/
 */
export const createTypeSafeContext = <A extends unknown | null>(
  initialValue?: A
): readonly [() => A & (unknown | null), React.Context<A | undefined>] => {
  const ctx = initialValue
    ? createContext<A | undefined>(initialValue)
    : createContext<A | undefined>(undefined);

  const useCtx = () => {
    const c = useContext(ctx);

    if (c === undefined) {
      throw new Error('useCtx must be inside a Provider with a value');
    }

    return c;
  };

  return [useCtx, ctx] as const; // 'as const' makes TypeScript infer a tuple
};
